// BETA-ONLY: Remove this feature when beta ends

import { CheckSquare, Square } from 'lucide-react';
import type { QuestionOption } from '../types/betaProfile';

interface MultiSelectGroupProps {
  options: QuestionOption[];
  values: string[];
  onChange: (values: string[]) => void;
  /** When this value is selected, all others are deselected (and vice versa) */
  exclusiveValue?: string;
  columns?: number;
}

export function MultiSelectGroup({
  options,
  values,
  onChange,
  exclusiveValue,
  columns = 2,
}: MultiSelectGroupProps): JSX.Element {
  const handleToggle = (optionValue: string): void => {
    // If the exclusive value is being selected, deselect everything else
    if (exclusiveValue && optionValue === exclusiveValue) {
      if (values.includes(optionValue)) {
        onChange([]);
      } else {
        onChange([optionValue]);
      }
      return;
    }

    // If selecting a non-exclusive value, remove the exclusive value
    const filtered = exclusiveValue
      ? values.filter((v) => v !== exclusiveValue)
      : values;

    if (filtered.includes(optionValue)) {
      onChange(filtered.filter((v) => v !== optionValue));
    } else {
      onChange([...filtered, optionValue]);
    }
  };

  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {options.map((option) => {
        const isSelected = values.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleToggle(option.value)}
            className={`
              p-3 rounded-lg border-2 text-left text-sm transition-all
              flex items-center gap-2
              ${isSelected
                ? 'bg-evergreen/10 text-evergreen border-evergreen font-semibold'
                : 'bg-white text-ink border-soft-neutral hover:border-evergreen/50'
              }
            `}
          >
            {isSelected ? (
              <CheckSquare className="w-4 h-4 flex-shrink-0" />
            ) : (
              <Square className="w-4 h-4 flex-shrink-0 text-muted" />
            )}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
